/**
 * HOME LANGUAGE SURVEY COMPONENT
 *
 * Main survey form for collecting home language information from students/families.
 * This component is central to the Home Language Survey application and handles:
 *
 * - Multi-step form validation using Formik and Yup
 * - Dynamic field requirements based on screening responses
 * - Language selection with ISO 639-3 support
 * - School selection and grade assignment
 * - Student demographic information collection
 * - Form submission and PDF generation
 * - Integration with English Learner identification process
 *
 * The survey helps schools comply with Title III requirements by identifying
 * students who may need English language support services.
 */

import React, { useEffect, useState } from 'react'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import Icon from '@mdi/react'
import { mdiLoading } from '@mdi/js'
// Modal and UI components
import SubmittingModal from '../modals/SubmittingModal'
import ImmigrantTooltip from './ImmigrantTooltip'
import EnglishLearnerTooltip from './EnglishLearnerTooltip'
import Selector from './Selector'
import SurveyInstructions from './SurveyInstructions'
import LanguageInformation from './LanguageInformation'
import RadioGroup from './RadioGroup'
import TextInput from './TextInput'
import { api, getSchoolOptions } from '../shared/ApiProvider'
import { emptyOptions } from '../compare/assets/default-objects'

/**
 * Utility function to safely convert any input to string
 * Handles null, undefined, arrays, and objects gracefully
 * Used for form data processing and validation
 */
function safeString(input) {
  if (typeof input === 'string') return input
  if (input === null || input === undefined) return ''
  if (Array.isArray(input)) {
    return input.map(safeString).join(' ')
  }
  if (typeof input === 'object') {
    return '[object]'
  }
  return String(input)
}

/**
 * FORM VALIDATION SCHEMA
 *
 * Uses Yup for comprehensive form validation with conditional requirements.
 * Key validation rules:
 * - Initial screening questions are always required
 * - Student details become required if screening indicates potential EL status
 * - Conditional validation based on wasScreened and isImmigrant responses
 * - Character limits on text fields (comments: 250 chars)
 */
const validationSchema = Yup.object().shape({
  // Initial screening questions - always required
  wasScreened: Yup.string().required('Please answer this question'),
  isImmigrant: Yup.string().required('Please answer this question'),

  // Student demographic fields - required if screening indicates potential EL
  firstName: Yup.string().when(['wasScreened', 'isImmigrant'], {
    is: (wasScreened, isImmigrant) => wasScreened !== 'N' || isImmigrant !== 'N',
    then: schema => schema.required('First name is required'),
    otherwise: schema => schema,
  }),
  lastName: Yup.string().when(['wasScreened', 'isImmigrant'], {
    is: (wasScreened, isImmigrant) => wasScreened !== 'N' || isImmigrant !== 'N',
    then: schema => schema.required('Last name is required'),
    otherwise: schema => schema,
  }),
  birthDate: Yup.date().when(['wasScreened', 'isImmigrant'], {
    is: (wasScreened, isImmigrant) => wasScreened !== 'N' || isImmigrant !== 'N',
    then: schema => schema.required('Birth date is required'),
    otherwise: schema => schema,
  }),
  sex: Yup.string().when(['wasScreened', 'isImmigrant'], {
    is: (wasScreened, isImmigrant) => wasScreened !== 'N' || isImmigrant !== 'N',
    then: schema => schema.required('Gender is required'),
    otherwise: schema => schema,
  }),
  bornInUSA: Yup.string().when(['wasScreened', 'isImmigrant'], {
    is: (wasScreened, isImmigrant) => wasScreened !== 'N' || isImmigrant !== 'N',
    then: schema => schema.required('You must answer whether the student was born in the USA or Puerto Rico'),
    otherwise: schema => schema,
  }),
  // Optional comment field with character limit
  comment: Yup.string().max(250, 'Comment must be 250 characters or less'),
})

/**
 * Grade level options for student assignment
 * Covers Pre-K through 12th grade to match Vermont education system
 */
// todo: make this import from default-objects; something's not right with how they interact
const grades = [
  { value: 'PK', label: 'Pre-K' },
  { value: 'K', label: 'Kindergarten' },
  { value: '1', label: 'Grade 1' },
  { value: '2', label: 'Grade 2' },
  { value: '3', label: 'Grade 3' },
  { value: '4', label: 'Grade 4' },
  { value: '5', label: 'Grade 5' },
  { value: '6', label: 'Grade 6' },
  { value: '7', label: 'Grade 7' },
  { value: '8', label: 'Grade 8' },
  { value: '9', label: 'Grade 9' },
  { value: '10', label: 'Grade 10' },
  { value: '11', label: 'Grade 11' },
  { value: '12', label: 'Grade 12' },
]

const Survey = () => {
  /**
   * STATE MANAGEMENT
   *
   * Component manages multiple pieces of state for:
   * - Reference data (languages, schools, ISO codes)
   * - Form submission status and UI state
   * - Conditional logic flags for survey flow
   */

  // Reference data loaded from API endpoints
  const [languages, setLanguages] = useState([]) // Available languages for selection
  const [iso639_3, setIso639_3] = useState([]) // ISO 639-3 language codes
  const [schools, setSchools] = useState(emptyOptions) // Vermont schools list
  const [isLoadingData, setIsLoadingData] = useState(true) // Loading state for initial data
  const [dataLoadError, setDataLoadError] = useState(null) // Error state for data loading

  // Form submission and UI state
  const [isSubmitting, setIsSubmitting] = useState(false) // Shows loading modal
  const [submitSuccessful, setSubmitSuccessful] = useState(false) // Success state
  const [showPDFLink, setShowPDFLink] = useState(false) // PDF generation link
  const [formData, setFormData] = useState(null) // Submitted form data

  // Conditional logic state for survey flow
  const [hasImmigrantStatus, setHasImmigrantStatus] = useState(null)
  const [suggestedLanguage, setSuggestedLanguage] = useState(null)
  const [isBornInUSA, setIsBornInUSA] = useState(null)

  /**
   * DATA INITIALIZATION
   *
   * Fetch reference data from backend API on component mount.
   * Loads:
   * - Languages: Available language options with codes
   * - ISO 639-3: International language standard codes
   * - Schools: Vermont schools for student assignment
   */
  useEffect(() => {
    const languageURL = '/public/languages'
    const iso639_3URL = '/public/ISO_639-3'

    setIsLoadingData(true)
    setDataLoadError(null)

    // Fetch all data in parallel
    Promise.all([api.get(languageURL), api.get(iso639_3URL), getSchoolOptions()])
      .then(([langResponse, lang3Response, schoolsResponse]) => {
        // Process languages
        const languages = langResponse.data.map(l => ({
          value: l.code,
          label: l.language + ' (' + l.code + ')',
        }))
        setLanguages(languages)

        // Process ISO 639-3 codes
        const iso639Languages = lang3Response.data.map(l => ({
          value: l.id,
          label: l.name,
        }))
        setIso639_3(iso639Languages)

        // Set schools
        setSchools(schoolsResponse)

        setIsLoadingData(false)
        console.log('Successfully loaded all reference data')
      })
      .catch(error => {
        console.error('Error loading reference data:', error)
        setDataLoadError(error.message || 'Failed to load form data. Please refresh the page.')
        setIsLoadingData(false)
      })
  }, [])

  /**
   * Modal and form reset functionality
   * Cleans up state after successful submission or user cancellation
   */
  const closeModalAndReset = resetForm => {
    setIsSubmitting(false)
    setSubmitSuccessful(false)
    setShowPDFLink(false)
    if (typeof resetForm === 'function') {
      console.log('Calling resetForm from closeModalAndReset')
      resetForm()
    } else {
      console.log('resetForm is not a function:', resetForm)
    }
    setFormData(null)
  }

  /**
   * Form reset handler for manual reset button
   * Delegates to Formik's resetForm function
   */
  const reset = () => {
    console.log('Reset button clicked')
  }

  /**
   * LANGUAGE SELECTOR FIELD DEFINITIONS
   *
   * Defines the key language questions for the home language survey.
   * Each field uses the same language options but serves different purposes
   * in determining English Learner eligibility.
   */
  const safeLanguages = Array.isArray(languages) ? languages : []
  const selectorFields = [
    {
      name: 'spokenInHome',
      label: 'What language(s) are spoken in your home?',
      options: safeLanguages,
      isMulti: true,
    },
    {
      name: 'usedMostOftenWithChild',
      label: 'What language do you most often speak to your child?',
      options: safeLanguages,
      isMulti: true,
    },
    {
      name: 'favored',
      label: 'What language does your child currently use most often at home?',
      options: safeLanguages,
      isMulti: true,
    },
    {
      name: 'parentLanguages',
      label: 'What are the native languages of the parent/guardians?',
      options: safeLanguages,
      isMulti: true,
    },
    {
      name: 'firstLearned',
      label: 'Which language did your child learn first?',
      options: safeLanguages,
      isMulti: true,
    },
  ]

  return (
    <div>
      {isLoadingData && (
        <div className="alert alert-info" role="alert">
          <div className="d-flex align-items-center">
            <Icon path={mdiLoading} spin color="blue" size={1} className="me-2" />
            <span>Loading form data...</span>
          </div>
        </div>
      )}

      {dataLoadError && (
        <div className="alert alert-danger" role="alert">
          <h5 className="alert-heading">Error Loading Form</h5>
          <p>{dataLoadError}</p>
          <button className="btn btn-danger" onClick={() => window.location.reload()}>
            Refresh Page
          </button>
        </div>
      )}

      {!isLoadingData && !dataLoadError && (
        <Formik
          initialValues={{
            screenerName: '',
            screenedDate: '',
            wasScreened: '',
            isImmigrant: '',
            firstName: '',
            lastName: '',
            birthDate: '',
            firstUSEnrollmentDate: '',
            permNumber: '',
            sex: '',
            bornInUSA: '',
            specificLanguage: null,
            spokenInHome: [],
            usedMostOftenWithChild: [],
            favored: [],
            parentLanguages: [],
            firstLearned: [],
            enrollingAt: null,
            enrollingAtDate: '',
            enrollingGrade: null,
            comment: '',
          }}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            setIsSubmitting(true)
            // Transform date fields to 'YYYY-MM-DDT00:00:00' format
            const fixDate = d => (d ? `${d}T00:00:00` : d)
            const payload = {
              ...values,
              birthDate: fixDate(values.birthDate),
              firstUSEnrollmentDate: fixDate(values.firstUSEnrollmentDate),
              screenedDate: fixDate(values.screenedDate),
              enrollingAtDate: fixDate(values.enrollingAtDate),
            }
            console.log('Submitting values:', payload)
            console.log('spokenInHome:', values.spokenInHome)
            console.log('usedMostOftenWithChild:', values.usedMostOftenWithChild)
            console.log('favored:', values.favored)
            console.log('parentLanguages:', values.parentLanguages)
            console.log('firstLearned:', values.firstLearned)
            console.log('enrollingAtDate:', values.enrollingAtDate)
            console.log('birthDate:', values.birthDate)
            try {
              await api.post('/public/survey', payload, {
                headers: { 'Content-Type': 'application/json' },
              })
              setSubmitSuccessful(true)
              setFormData(payload)
              setShowPDFLink(true)
            } catch (error) {
              console.error(error.message)
              setIsSubmitting(false)
            }
            setSubmitting(false)
          }}
        >
          {({ values, setFieldValue, isSubmitting, errors, touched, resetForm }) => {
            // Handler for immigrant status change
            const handleImmigrantStatusChange = event => {
              setHasImmigrantStatus(event.target.value)
              setFieldValue('isImmigrant', event.target.value)
            }

            // Effect for suggested language
            useEffect(() => {
              const controller = new AbortController()
              if (values && values.specificLanguage && values.specificLanguage.value) {
                api
                  .get(`/public/crosswalkTable/${values.specificLanguage.value}`, {
                    signal: controller.signal,
                  })
                  .then(
                    resp => {
                      if (resp.data) {
                        setSuggestedLanguage(resp.data.suggestedLanguage.language)
                      } else {
                        setSuggestedLanguage('No suggestion found')
                      }
                    },
                    error => {
                      if (error.name !== 'CanceledError') {
                        console.error(error)
                      }
                    }
                  )
              }
              return () => controller.abort()
            }, [values && values.specificLanguage])

            return (
              <div>
                <Form>
                  <SurveyInstructions />
                  <div className="card border-dark">
                    <div className="card-body" id="screenQuestion">
                      <div className="card-header text-white bg-primary">Screening Questions</div>
                      <div className="card-body">
                        <div className="form-group row">
                          <div className="col">
                            <div className={`form-group ${touched?.wasScreened && errors?.wasScreened ? 'is-invalid' : ''}`}>
                              <Field name="wasScreened">
                                {({ field }) => (
                                  <RadioGroup
                                    name="wasScreened"
                                    label={
                                      <span>
                                        Was this student screened for English Language Proficiency and identified as an English Learner
                                        (EL)? <EnglishLearnerTooltip />
                                      </span>
                                    }
                                    options={[
                                      { value: 'Y', label: 'Yes' },
                                      { value: 'N', label: 'No' },
                                    ]}
                                    error={touched?.wasScreened && errors?.wasScreened}
                                    field={field}
                                  />
                                )}
                              </Field>
                            </div>
                            {touched?.wasScreened && errors?.wasScreened && (
                              <div className="invalid-feedback">{errors?.wasScreened?.message}</div>
                            )}
                          </div>
                        </div>
                        <div className="form-group row">
                          <div className="col">
                            <div className={`form-group ${touched?.isImmigrant && errors?.isImmigrant ? 'is-invalid' : ''}`}>
                              <Field name="isImmigrant">
                                {({ field }) => (
                                  <RadioGroup
                                    name="isImmigrant"
                                    label={
                                      <span>
                                        Does the student meet the ESSA definition of “Immigrant Children and Youth? <ImmigrantTooltip />
                                      </span>
                                    }
                                    options={[
                                      { value: 'Y', label: 'Yes' },
                                      { value: 'N', label: 'No' },
                                    ]}
                                    error={touched?.isImmigrant && errors?.isImmigrant}
                                    field={field}
                                    onChange={handleImmigrantStatusChange}
                                  />
                                )}
                              </Field>
                            </div>
                            {touched?.isImmigrant && errors?.isImmigrant && (
                              <div className="invalid-feedback">{errors?.isImmigrant?.message}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Only render the rest of the form if both screening questions are answered */}
                    {['Y', 'N'].includes(values.wasScreened) &&
                      ['Y', 'N'].includes(values.isImmigrant) &&
                      (values.wasScreened === 'N' && values.isImmigrant === 'N' ? (
                        <div className="card-body">
                          <p className="text-danger">
                            You do not have to complete this survey. Please contact{' '}
                            <a href="mailto:HeidiJo.Bartlett@vermont.gov">HeidiJo.Bartlett@vermont.gov</a> if you have any questions.
                          </p>
                          <button
                            type="button"
                            className="btn btn-secondary mt-3"
                            onClick={() => {
                              resetForm()
                              setHasImmigrantStatus && setHasImmigrantStatus(null)
                              setSuggestedLanguage && setSuggestedLanguage(null)
                              setIsBornInUSA && setIsBornInUSA(null)
                            }}
                          >
                            Reset Form
                          </button>
                        </div>
                      ) : (
                        <div className="card-body">
                          <div className="card-header text-white bg-primary">Student Information</div>
                          <div className="card-body">
                            <div className="form-group row">
                              <div className="col-12 col-sm-6 col-md-6 mb-1 mb-md-2">
                                <Field name="firstName">
                                  {({ field }) => (
                                    <TextInput {...field} label="First name:" error={touched?.firstName && errors?.firstName} />
                                  )}
                                </Field>
                              </div>
                              <div className="col-12 col-sm-6 col-md-6 mb-1 mb-md-2">
                                <Field name="lastName">
                                  {({ field }) => <TextInput {...field} label="Last Name:" error={touched?.lastName && errors?.lastName} />}
                                </Field>
                              </div>
                              <div className="col-12 col-sm-6 col-md mb-1 mb-md-2">
                                <Field name="birthDate">
                                  {({ field }) => (
                                    <TextInput
                                      {...field}
                                      label="Date of Birth:"
                                      type="date"
                                      error={touched?.birthDate && errors?.birthDate}
                                    />
                                  )}
                                </Field>
                              </div>
                              <div className="col-12 col-sm-6 col-md px-0 px-md-3 mb-1 mb-md-2">
                                <div className={`form-group ${touched?.sex && errors?.sex ? 'is-invalid' : ''}`}>
                                  <Field name="sex">
                                    {({ field }) => (
                                      <RadioGroup
                                        name="sex"
                                        label="Gender:"
                                        options={[
                                          { value: 'M', label: 'Male' },
                                          { value: 'F', label: 'Female' },
                                        ]}
                                        error={touched?.sex && errors?.sex}
                                        field={field}
                                      />
                                    )}
                                  </Field>
                                  {touched?.sex && errors?.sex && <div className="invalid-feedback">{errors?.sex.message}</div>}
                                </div>
                              </div>
                            </div>
                            <div className="form-row">
                              <div className="col mt-0 mt-md-0 mb-1 mb-md-2">
                                <div>
                                  <Field name="bornInUSA">
                                    {({ field }) => (
                                      <RadioGroup
                                        name="bornInUSA"
                                        label="Was the student born in the USA or Puerto Rico?"
                                        options={[
                                          { value: 'Y', label: 'Yes' },
                                          { value: 'N', label: 'No' },
                                        ]}
                                        error={touched?.bornInUSA && errors?.bornInUSA}
                                        field={field}
                                      />
                                    )}
                                  </Field>
                                </div>
                                {touched?.bornInUSA && errors?.bornInUSA && (
                                  <div className="invalid-feedback d-block">{errors.bornInUSA}</div>
                                )}
                              </div>
                            </div>
                            <div className="form-group row">
                              <div className="col">
                                <Field name="firstUSEnrollmentDate">
                                  {({ field }) => (
                                    <TextInput
                                      {...field}
                                      label="Date Student first began in any U.S. school:"
                                      type="date"
                                      error={touched?.firstUSEnrollmentDate && errors?.firstUSEnrollmentDate}
                                    />
                                  )}
                                </Field>
                              </div>
                            </div>
                            <div className="form-group row screeningDetails">
                              <div className="col-12 col-md mb-1 mb-md-2">
                                <Field name="screenerName">
                                  {({ field }) => (
                                    <TextInput
                                      {...field}
                                      label="School Contact Name and Email"
                                      placeholder="Name and email"
                                      error={touched?.screenerName && errors?.screenerName}
                                    />
                                  )}
                                </Field>
                              </div>
                              <div className="col-12 col-md mb-1 mb-md-2">
                                <Field name="screenedDate">
                                  {({ field }) => (
                                    <TextInput
                                      {...field}
                                      label="Date Student Screened"
                                      type="date"
                                      error={touched?.screenedDate && errors?.screenedDate}
                                    />
                                  )}
                                </Field>
                              </div>
                            </div>
                            <div className="form-group row">
                              <div className="col-12 col-sm-6 col-md mb-1 mb-md-2">
                                <Field name="permNumber">
                                  {({ field }) => (
                                    <TextInput
                                      {...field}
                                      label="PERM Number"
                                      maxLength={7}
                                      pattern="\d{7}"
                                      error={touched?.permNumber && errors?.permNumber}
                                      placeholder="Enter 7-digit PERM Number (optional)"
                                      autoComplete="off"
                                    />
                                  )}
                                </Field>
                              </div>
                            </div>
                          </div>
                          <div className="card-header text-white bg-primary">Family Information</div>
                          <div className="card-body">
                            <h6 className="form-group row">
                              <div className="col-12 col-sm-6 col-md mb-1 mb-md-2">
                                <label>
                                  Primary Language <br /> (other than English)
                                </label>
                              </div>
                              <div className="col-12 col-md-6">
                                <Field name="specificLanguage">
                                  {({ field, form }) => (
                                    <Selector
                                      name={field.name}
                                      value={field.value}
                                      onChange={option => form.setFieldValue(field.name, option || null)}
                                      options={iso639_3}
                                      error={touched.specificLanguage && errors.specificLanguage}
                                      isSearchable={true}
                                      className="basic-multi-select"
                                      classNamePrefix="select"
                                      filterOption={(candidate, input) => {
                                        const safeInput = safeString(input)
                                        if (!safeInput) return true
                                        const searchWords = safeInput.toLowerCase().split(/\s+/)
                                        const label = safeString(candidate && candidate.label ? candidate.label : '').toLowerCase()
                                        return searchWords.some(word => label.includes(word))
                                      }}
                                    />
                                  )}
                                </Field>
                                {touched.specificLanguage && errors.specificLanguage && (
                                  <div className="invalid-feedback d-block">
                                    {errors.specificLanguage.message || errors.specificLanguage}
                                  </div>
                                )}
                              </div>
                              <div className="col-12 col-md-3 alert-info">
                                {suggestedLanguage ? `Suggested language : ${suggestedLanguage}` : null}
                              </div>
                            </h6>
                            {selectorFields.map(field => (
                              <div className="form-group row" key={field.name}>
                                <div className="col-12 col-md-3">
                                  <label htmlFor={String(field.name)}>{String(field.label)}</label>
                                </div>
                                <div className="col-12 col-md">
                                  <Field name={String(field.name)}>
                                    {({ field: ctrlField, form }) => (
                                      <Selector
                                        name={field.name}
                                        value={ctrlField.value}
                                        onChange={option => form.setFieldValue(field.name, field.isMulti ? option || [] : option || null)}
                                        options={field.options}
                                        error={touched[field.name] && errors[field.name]}
                                        isMulti={field.isMulti}
                                        closeMenuOnSelect={false}
                                        filterOption={(candidate, input) => {
                                          const safeInput = safeString(input)
                                          if (!safeInput) return true
                                          const searchWords = safeInput.toLowerCase().split(/\s+/)
                                          const label = safeString(candidate.label).toLowerCase()
                                          return searchWords.some(word => label.includes(word))
                                        }}
                                      />
                                    )}
                                  </Field>
                                  {touched[field.name] && errors[field.name] && (
                                    <div className="invalid-feedback" id={`${field.name}-error`}>
                                      {errors[field.name].message || errors[field.name]}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                            <LanguageInformation />
                          </div>
                          <div className="card-header text-white bg-primary">School Information</div>
                          <div className="card-body">
                            <div className="form-group row mt-3">
                              <div className="col-12 col-md-4 col-lg-6">
                                <label>
                                  What school <em>will</em> the student attend?
                                </label>
                                <Field name="enrollingAt">
                                  {({ field, form }) => {
                                    // Always pass the full object to Selector
                                    const selectedSchool =
                                      schools && field.value && field.value.value
                                        ? schools.find(opt => opt.value === field.value.value) || field.value
                                        : field.value || null
                                    return (
                                      <Selector
                                        name={field.name}
                                        value={selectedSchool}
                                        onChange={option => form.setFieldValue(field.name, option || null)}
                                        options={schools}
                                        error={touched.enrollingAt && errors.enrollingAt}
                                      />
                                    )
                                  }}
                                </Field>
                                {touched.enrollingAt && errors.enrollingAt && (
                                  <div className="invalid-feedback">{errors.enrollingAt.message || errors.enrollingAt}</div>
                                )}
                              </div>
                              <div className="col-12 col-md-4 col-lg-6">
                                <label>What grade will the student enter?</label>
                                <Field name="enrollingGrade">
                                  {({ field, form }) => {
                                    // Always pass the full object to Selector
                                    const selectedGrade =
                                      grades && field.value && field.value.value
                                        ? grades.find(opt => opt.value === field.value.value) || field.value
                                        : field.value || null
                                    return (
                                      <Selector
                                        name={field.name}
                                        value={selectedGrade}
                                        onChange={option => form.setFieldValue(field.name, option || null)}
                                        options={grades}
                                        error={touched.enrollingGrade && errors.enrollingGrade}
                                      />
                                    )
                                  }}
                                </Field>
                                {touched.enrollingGrade && errors.enrollingGrade && (
                                  <div className="invalid-feedback">{errors.enrollingGrade.message || errors.enrollingGrade}</div>
                                )}
                              </div>
                            </div>
                            <div className="form-group row">
                              <div className="col-12 col-md-4">
                                <Field name="enrollingAtDate">
                                  {({ field }) => (
                                    <TextInput
                                      {...field}
                                      label="Beginning date in this school"
                                      type="date"
                                      error={touched.enrollingAtDate && errors.enrollingAtDate}
                                    />
                                  )}
                                </Field>
                                {touched.enrollingAtDate && errors.enrollingAtDate && (
                                  <div className="invalid-feedback">{errors.enrollingAtDate}</div>
                                )}
                              </div>
                            </div>
                            <div className="form-group row">
                              <div className="col-12">
                                <label htmlFor="comment">Additional Comments:</label>
                                <Field name="comment">
                                  {({ field }) => (
                                    <div>
                                      <textarea
                                        {...field}
                                        className={`form-control ${touched.comment && errors.comment ? 'is-invalid' : ''}`}
                                        id="comment"
                                        rows="4"
                                        placeholder="Enter any additional comments here..."
                                        maxLength={250}
                                      />
                                      <div className="d-flex justify-content-between mt-1">
                                        <small className="text-muted">{250 - (field.value?.length || 0)} characters remaining</small>
                                        {touched.comment && errors.comment && (
                                          <div className="invalid-feedback d-block">{errors.comment}</div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </Field>
                              </div>
                            </div>
                          </div>
                          <div className="card-footer">
                            <div className="col-12 col-md-3 text-center d-flex justify-content-center flex-column">
                              <button type="submit" className="btn btn-outline-primary">
                                Submit to AOE
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </Form>
                {showPDFLink && (
                  <SubmittingModal
                    isSubmitting={isSubmitting}
                    submitSuccessful={submitSuccessful}
                    data={formData}
                    showPDFLink={showPDFLink}
                    close={() => closeModalAndReset(resetForm)}
                  />
                )}
              </div>
            )
          }}
        </Formik>
      )}
    </div>
  )
}

export default Survey
